import React, { useMemo } from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import { mapListingToCardProps } from '../../../util/listingCardAdapter';

import { MarketplaceListingCard } from '../../../components/MarketplaceListingCard';

type ProfileListingsSectionProps = {
  listings: any[];
  /** Profile owner: used to populate seller badges on each card. */
  profileUser: any;
};

const ProfileListingsSection = ({ listings, profileUser }: ProfileListingsSectionProps) => {
  const config = useConfiguration() as any;
  const categories: Array<{ id: string; name: string }> =
    config?.categoryConfiguration?.categories || [];

  const cards = useMemo(
    () => listings.map(l => mapListingToCardProps(l, { categories, sellerOverride: profileUser })),
    [listings, categories, profileUser]
  );

  if (cards.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase tracking-tight">
            <FormattedMessage id="CustomProfilePage.listingsTitle" />
          </h2>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage id="CustomProfilePage.listingsSubtitle" />
          </p>
        </div>
        <div
          className="rounded-full border border-border bg-white px-3 py-1 text-xs font-heading uppercase tracking-[0.18em] text-muted-foreground"
          data-testid="text-public-profile-listing-count"
        >
          <FormattedMessage
            id="CustomProfilePage.listingsCount"
            values={{ count: cards.length }}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map(card => (
          <MarketplaceListingCard
            key={card.id}
            listingId={card.id}
            listingSlug={card.slug}
            title={card.title}
            image={card.imgUrl}
            preloadSrc={card.preloadSrc}
            priceLabel={card.priceLabel}
            condition={card.condition}
            location={card.location}
            category={card.category}
            posted={card.posted}
            sellerRating={card.reviewAvg}
            sellerReviews={card.reviewCount}
            membershipTier={card.membershipTier}
            isVerified={card.isVerified}
            dataTestId={`card-public-profile-listing-${card.id}`}
            titleTestId={`text-public-profile-listing-title-${card.id}`}
            priceTestId={`text-public-profile-listing-price-${card.id}`}
          />
        ))}
      </div>
    </section>
  );
};

export default ProfileListingsSection;
